import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, Star, Loader } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

const COMPLEXITY_COLORS = {
  low: "bg-green-100 text-green-800",
  medium: "bg-yellow-100 text-yellow-800",
  high: "bg-red-100 text-red-800"
};

export default function PortSelector({ onSelect }) {
  const [search, setSearch] = useState("");
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("favoriteMarinas") || "[]");
    } catch {
      return [];
    }
  });

  const { data: marinas = [] } = useQuery({
    queryKey: ["marinas"],
    queryFn: () => base44.entities.Marina.list("-rating", 50)
  });

  const filteredMarinas = marinas.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.country.toLowerCase().includes(search.toLowerCase()) ||
    m.location.toLowerCase().includes(search.toLowerCase())
  );

  const favoriteMarinas = marinas.filter(m => favorites.includes(m.id));

  const getComplexity = (country) => {
    const highComplexity = ["China", "Russia", "Iran", "North Korea"];
    const mediumComplexity = ["India", "Brazil", "Mexico"];
    return highComplexity.includes(country)
      ? "high"
      : mediumComplexity.includes(country)
      ? "medium"
      : "low";
  };

  const toggleFavorite = (e, marinaId) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = prev.includes(marinaId)
        ? prev.filter(id => id !== marinaId)
        : [...prev, marinaId];
      localStorage.setItem("favoriteMarinas", JSON.stringify(next));
      return next;
    });
  };

  const MarinaCard = ({ marina }) => {
    const complexity = getComplexity(marina.country);
    const isFavorite = favorites.includes(marina.id);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        onClick={() => onSelect(marina)}
      >
        <Card className="cursor-pointer hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-sm truncate">{marina.name}</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => toggleFavorite(e, marina.id)}
                    className="p-0 h-6 w-6"
                  >
                    <Star className={`w-4 h-4 ${isFavorite ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`} />
                  </Button>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                  <MapPin className="w-3 h-3" />
                  {marina.location}, {marina.country}
                </div>
                <div className="flex flex-wrap gap-1">
                  <Badge className={COMPLEXITY_COLORS[complexity]} variant="outline">
                    {complexity === "high" ? "⚠️" : complexity === "medium" ? "⚡" : "✓"} {complexity} entry
                  </Badge>
                  {marina.rating && (
                    <Badge variant="outline" className="text-xs">
                      ⭐ {marina.rating.toFixed(1)}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search marina or port…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Favorites */}
      {!search && favoriteMarinas.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase">⭐ Saved Favorites</h3>
          <div className="grid gap-3">
            {favoriteMarinas.map(marina => (
              <MarinaCard key={marina.id} marina={marina} />
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase">
          {search ? "Search Results" : "Popular Ports & Marinas"}
        </h3>
        {filteredMarinas.length === 0 ? (
          <Card className="bg-muted/50">
            <CardContent className="py-8 text-center text-muted-foreground">
              <p className="text-sm">No ports found. Try a different search.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredMarinas.map(marina => (
              <MarinaCard key={marina.id} marina={marina} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}